import { create } from 'zustand'

/**
 * The little state the client owns (PLAN.md §12, §13).
 *
 * Deliberately small. The funnel's real position is `step` on the server,
 * derived from the session cookie, and the client asks for it rather than
 * tracking it — a client-side step counter is how a user ends up on step 3 with
 * no CRM lead behind them.
 *
 * What lives here is only what the server cannot give back:
 *
 *   - `challengeId`, the opaque handle to an in-flight OTP. The server will not
 *     re-issue it, so losing it on a refresh means starting the code over.
 *   - `email`, purely to render "we sent a code to …". It is never sent again
 *     after `/auth/otp/send`; everything downstream is addressed by the cookie
 *     (findings #2, #3).
 *
 * Both are held in `sessionStorage`, not `localStorage`: they belong to this
 * tab's attempt and must not outlive the tab or leak into another one.
 */

const KEY = 'kcm.funnel'

interface Persisted {
  challengeId: string | null
  email: string | null
}

interface FunnelState extends Persisted {
  startChallenge: (challengeId: string, email: string) => void
  clearChallenge: () => void
  reset: () => void
}

const EMPTY: Persisted = { challengeId: null, email: null }

function load(): Persisted {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return EMPTY
    const parsed = JSON.parse(raw) as Partial<Persisted>
    return {
      challengeId: typeof parsed.challengeId === 'string' ? parsed.challengeId : null,
      email: typeof parsed.email === 'string' ? parsed.email : null,
    }
  } catch {
    // Unparseable or unavailable storage means starting fresh, which is the
    // same outcome as a new tab. Never worth an error screen.
    return EMPTY
  }
}

function save(state: Persisted): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(state))
  } catch {
    /* private browsing; the flow still works within this page */
  }
}

export const useFunnel = create<FunnelState>((set) => ({
  ...load(),

  startChallenge: (challengeId, email) => {
    const next = { challengeId, email }
    save(next)
    set(next)
  },

  /** Called once the code is accepted — the handle is spent and worth nothing. */
  clearChallenge: () => {
    set((s) => {
      const next = { challengeId: null, email: s.email }
      save(next)
      return next
    })
  },

  reset: () => {
    save(EMPTY)
    set(EMPTY)
  },
}))

/** `j••••@example.com` — enough to recognise, not enough to harvest. */
export function maskEmail(email: string): string {
  const at = email.indexOf('@')
  if (at < 1) return email
  const local = email.slice(0, at)
  const head = local[0] ?? ''
  return `${head}${'•'.repeat(Math.min(Math.max(local.length - 1, 1), 6))}${email.slice(at)}`
}
